/**
 * Retournement automatique (design system, MinuteurDeRetournement) : compte à rebours au
 * recto ; à 0 s la carte se retourne sans être notée. En pause pendant un dialogue ou quand
 * la fenêtre passe en arrière-plan.
 */
import { useEffect, useRef, useState } from 'react';
import { Icone } from '../composants/Icone';
import { cx } from '../outils';

export function MinuteurDeRetournement({
  duree,
  affichage,
  enPause,
  onEcoule,
}: {
  duree: number;
  affichage: 'numerique' | 'camembert' | 'masque';
  enPause: boolean;
  onEcoule: () => void;
}) {
  const [restantMs, setRestantMs] = useState(duree * 1000);
  const [cache, setCache] = useState(document.hidden);
  const rappel = useRef(onEcoule);
  rappel.current = onEcoule;
  const pause = enPause || cache;

  useEffect(() => {
    const visibilite = () => setCache(document.hidden);
    document.addEventListener('visibilitychange', visibilite);
    return () => document.removeEventListener('visibilitychange', visibilite);
  }, []);

  useEffect(() => {
    if (pause || restantMs <= 0) return;
    const debut = performance.now();
    const depart = restantMs;
    const intervalle = window.setInterval(() => {
      const reste = Math.max(0, depart - (performance.now() - debut));
      setRestantMs(reste);
      if (reste <= 0) {
        window.clearInterval(intervalle);
        rappel.current();
      }
    }, 100);
    return () => window.clearInterval(intervalle);
    // Le décompte repart de la valeur restante à chaque reprise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pause]);

  const secondes = Math.ceil(restantMs / 1000);
  const etat = restantMs <= 0 ? 'termine' : pause ? 'pause' : 'en-cours';
  return (
    <div className="fc-ecran-seance__minuteur">
      <span
        className={cx(
          'fc-minuteur-de-retournement fc-minuteur-de-retournement--sous-la-carte',
          affichage !== 'numerique' && `fc-minuteur-de-retournement--${affichage}`,
        )}
        role="timer"
        aria-label="Retournement automatique"
        data-etat={etat}
        style={{ ['--part' as string]: ((restantMs / (duree * 1000)) * 100).toFixed(1) }}
      >
        <span className="fc-minuteur-de-retournement__decompte">
          <Icone nom="timer" className="fc-minuteur-de-retournement__icone" />
          <svg
            aria-hidden="true"
            className="fc-progression__camembert fc-minuteur-de-retournement__camembert"
            viewBox="0 0 14 14"
          >
            <circle className="fc-progression__contour" cx="7" cy="7" r="6.25" />
            <circle className="fc-progression__secteur" cx="7" cy="7" r="2.25" />
          </svg>
          <span className="fc-minuteur-de-retournement__valeur">{secondes}&nbsp;s</span>
        </span>
        <span className="fc-minuteur-de-retournement__pause">
          <Icone nom="circle-pause" />
          En pause
        </span>
      </span>
    </div>
  );
}
